import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, Globe, MapPin, ArrowLeft, Check } from "lucide-react";
import { CATEGORY_LABELS, CATEGORY_ICONS } from "./listing/constants";
import { PhotoLightbox } from "./listing/PhotoLightbox";
import { trackAdvertiser } from "./listing/trackAdvertiser";

export default function AdvertiserProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [advertiser, setAdvertiser] = useState(null);
  const [status, setStatus] = useState("loading");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [formState, setFormState] = useState("idle");
  const [formError, setFormError] = useState("");
  const [avatarError, setAvatarError] = useState(false);
  const [heroError, setHeroError] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const tracked = useRef(false);

  useEffect(() => {
    let active = true;
    api.get(`/public/advertisers/${slug}`)
      .then((res) => { if (active) { setAdvertiser(res.data); setStatus("ready"); } })
      .catch(() => { if (active) setStatus("not_found"); });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (!advertiser?.id || tracked.current) return;
    tracked.current = true;
    trackAdvertiser(advertiser.id, "profile_view");
  }, [advertiser]);

  const submit = async (e) => {
    e.preventDefault();
    setFormState("submitting");
    setFormError("");
    try {
      await api.post(`/public/advertisers/${slug}/inquiry`, form);
      setFormState("success");
    } catch (err) {
      setFormError(err.response?.data?.detail || "Something went wrong — please try again.");
      setFormState("idle");
    }
  };

  if (status === "loading") {
    return <div data-testid="advertiser-profile-loading" className="min-h-screen grid place-items-center bg-[#FAF9F6] text-[#667085]">Loading…</div>;
  }
  if (status === "not_found") {
    return (
      <div data-testid="advertiser-profile-not-found" className="min-h-screen grid place-items-center bg-[#FAF9F6] text-center px-6">
        <div>
          <p className="font-serif-display text-2xl text-[#202833]">Profile not found</p>
          <p className="text-sm text-[#667085] mt-2">This advertiser's profile is unavailable.</p>
        </div>
      </div>
    );
  }

  const Icon = CATEGORY_ICONS[advertiser.category];
  const categoryLabel = CATEGORY_LABELS[advertiser.category] || "Local Expert";
  const heroImg = !heroError && (advertiser.cover_photo_url || advertiser.profile_photo_url || advertiser.logo_url);
  const avatarImg = !avatarError && (advertiser.profile_photo_url || advertiser.logo_url);

  return (
    <div data-testid="advertiser-profile-page" className="min-h-screen bg-[#FAF9F6]">
      <div className="relative h-56 sm:h-72 w-full bg-[#202833] overflow-hidden">
        {heroImg && <img src={heroImg} alt="" onError={() => setHeroError(true)} className="absolute inset-0 w-full h-full object-cover opacity-70" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#111820] via-[#111820]/40 to-transparent" />
        <button type="button" onClick={() => (window.history.length > 2 ? navigate(-1) : navigate("/"))} data-testid="advertiser-profile-back-link" className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-white/90 text-sm bg-black/30 rounded-full px-3 py-1.5 hover:bg-black/50 transition"><ArrowLeft size={14} />Back</button>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8 -mt-14 sm:-mt-16 pb-20">
        <div className="bg-white rounded-2xl border border-[#E7E4DE] shadow-sm p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            {avatarImg ? (
              <img src={avatarImg} alt={advertiser.business_name} onError={() => setAvatarError(true)} className="h-24 w-24 rounded-full object-cover border-4 border-white shadow-md -mt-16 sm:-mt-20 bg-white" />
            ) : (
              <div className="h-24 w-24 rounded-full border-4 border-white shadow-md -mt-16 sm:-mt-20 bg-[#F8F7F4] grid place-items-center text-2xl font-serif-display text-[#667085]">{advertiser.business_name?.[0] || "?"}</div>
            )}
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#C7A361]/15 text-[#8a6d2f] text-[10px] font-semibold uppercase tracking-[0.1em] px-3 py-1">{Icon && <Icon size={11} />}{categoryLabel}</span>
              <h1 data-testid="advertiser-profile-name" className="mt-2 font-serif-display text-2xl sm:text-3xl text-[#202833]">{advertiser.business_name}</h1>
              {advertiser.headline && <p className="text-sm text-[#667085] mt-1">{advertiser.headline}</p>}
              {advertiser.license_number && <p data-testid="advertiser-profile-license" className="text-xs text-[#98A2B3] mt-0.5">License #{advertiser.license_number}</p>}
            </div>
          </div>

          {advertiser.description && <p data-testid="advertiser-profile-description" className="mt-6 text-sm leading-relaxed text-[#475467]">{advertiser.description}</p>}

          <div className="mt-6 flex flex-wrap gap-2.5">
            {advertiser.contact_phone && <a href={`tel:${advertiser.contact_phone}`} data-testid="advertiser-profile-phone" className="inline-flex items-center gap-1.5 rounded-full border border-[#E7E4DE] text-[#202833] text-sm font-medium px-4 py-2 hover:bg-[#F8F7F4] transition"><Phone size={14} />{advertiser.contact_phone}</a>}
            {advertiser.contact_email && <a href={`mailto:${advertiser.contact_email}`} data-testid="advertiser-profile-email" className="inline-flex items-center gap-1.5 rounded-full border border-[#E7E4DE] text-[#202833] text-sm font-medium px-4 py-2 hover:bg-[#F8F7F4] transition"><Mail size={14} />{advertiser.contact_email}</a>}
            {advertiser.destination_url && <a href={advertiser.destination_url} target="_blank" rel="noreferrer" data-testid="advertiser-profile-website" className="inline-flex items-center gap-1.5 rounded-full border border-[#E7E4DE] text-[#202833] text-sm font-medium px-4 py-2 hover:bg-[#F8F7F4] transition"><Globe size={14} />Website</a>}
            {advertiser.service_area && <span data-testid="advertiser-profile-service-area" className="inline-flex items-center gap-1.5 rounded-full bg-[#F8F7F4] text-[#667085] text-sm px-4 py-2"><MapPin size={14} />{advertiser.service_area}</span>}
          </div>
          {advertiser.disclosure_text && <p className="mt-4 text-[11px] text-[#98A2B3]">{advertiser.disclosure_text}</p>}
        </div>

        {advertiser.bio && (
          <div className="mt-6 bg-white rounded-2xl border border-[#E7E4DE] shadow-sm p-6 sm:p-8">
            <h2 className="font-serif-display text-xl text-[#202833]">About {advertiser.business_name}</h2>
            <p data-testid="advertiser-profile-bio" className="mt-3 text-sm leading-relaxed text-[#475467] whitespace-pre-line">{advertiser.bio}</p>
          </div>
        )}

        {advertiser.gallery_photos?.length > 0 && (
          <div className="mt-6 bg-white rounded-2xl border border-[#E7E4DE] shadow-sm p-6 sm:p-8">
            <h2 className="font-serif-display text-xl text-[#202833]">Photo Gallery</h2>
            <div data-testid="advertiser-profile-gallery" className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2.5">
              {advertiser.gallery_photos.map((url, i) => (
                <button key={`${url}-${i}`} type="button" data-testid={`advertiser-profile-gallery-photo-${i}`} onClick={() => setLightboxIndex(i)} className="aspect-square rounded-lg overflow-hidden border border-[#E7E4DE] hover:opacity-90 transition">
                  <img src={url} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 bg-white rounded-2xl border border-[#E7E4DE] shadow-sm p-6 sm:p-8">
          <h2 className="font-serif-display text-xl text-[#202833]">Contact {advertiser.business_name}</h2>
          {formState === "success" ? (
            <div data-testid="advertiser-profile-form-success" className="mt-4 text-center py-6">
              <div className="mx-auto h-12 w-12 rounded-full bg-[#C7A361]/15 grid place-items-center"><Check size={22} className="text-[#C7A361]" /></div>
              <p className="mt-3 text-sm text-[#667085]">Message sent — {advertiser.business_name} will reach out soon.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3 mt-4">
              <div><Label className="text-xs text-[#667085]">Your name</Label><Input data-testid="advertiser-profile-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-lg border-[#E7E4DE]" /></div>
              <div><Label className="text-xs text-[#667085]">Email</Label><Input data-testid="advertiser-profile-form-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-lg border-[#E7E4DE]" /></div>
              <div><Label className="text-xs text-[#667085]">Phone (optional)</Label><Input data-testid="advertiser-profile-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-lg border-[#E7E4DE]" /></div>
              <div><Label className="text-xs text-[#667085]">Message</Label><Textarea data-testid="advertiser-profile-form-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-lg border-[#E7E4DE]" /></div>
              {formError && <p data-testid="advertiser-profile-form-error" className="text-xs text-red-600">{formError}</p>}
              <Button data-testid="advertiser-profile-form-submit" type="submit" disabled={formState === "submitting"} className="w-full rounded-full bg-[#C7A361] text-[#111820] hover:brightness-95 font-medium">{formState === "submitting" ? "Sending…" : "Send Message"}</Button>
            </form>
          )}
        </div>
      </div>
      {lightboxIndex !== null && (
        <PhotoLightbox photos={(advertiser.gallery_photos || []).map((url, i) => ({ id: i, url }))} index={lightboxIndex} onIndexChange={setLightboxIndex} onClose={() => setLightboxIndex(null)} />
      )}
    </div>
  );
}
